import { ReactNode, Dispatch, SetStateAction, CSSProperties } from "react";
import { CheckIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../command";
import { PopoverContent } from "../popover";
import { Spinner } from "../spinner";
import { useStringSelectionPopover } from "./useStringSelectionPopover";
import { i18n } from "@tellescope/i18n";

export interface StringSelectionPopoverOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface StringSelectionPopoverProps {
  options: StringSelectionPopoverOption[];
  selectedValues: string[];
  onToggle: (value: string) => void;

  searchPlaceholder?: string;
  searchQuery?: string;
  setSearchQuery?: Dispatch<SetStateAction<string>>;
  searchKeywords?: (option: StringSelectionPopoverOption) => string[];
  shouldFilter?: boolean;
  showSearch?: boolean;

  creator?: {
    render: (inputValue: string) => ReactNode;
    isValidNewOption?: (inputValue: string, selectedValues: string[]) => boolean;
    onCreate: (value: string) => void;
  };

  emptyText?: string;
  isLoading?: boolean;
  showCheckIndicators?: boolean;
  maxCount?: number;
  renderOption?: (option: StringSelectionPopoverOption, isSelected: boolean) => ReactNode;

  className?: string;
  style?: CSSProperties;
  align?: "start" | "center" | "end";
}

export const StringSelectionPopover = ({
  options,
  selectedValues,
  onToggle,
  searchPlaceholder = i18n.t("Search...", { ns: "ui" }),
  searchQuery,
  setSearchQuery,
  searchKeywords,
  shouldFilter = true,
  showSearch = true,
  creator,
  emptyText = i18n.t("No results found.", { ns: "ui" }),
  isLoading,
  showCheckIndicators = true,
  maxCount,
  renderOption,
  className,
  style,
  align = "start",
}: StringSelectionPopoverProps) => {
  const { search, setSearch, handleToggle, handleCreate, canCreate, handleKeyDown } = useStringSelectionPopover({
    selectedValues,
    onToggle,
    maxCount,
    searchQuery,
    setSearchQuery,
    creator: creator
      ? {
          isValidNewOption: creator.isValidNewOption,
          onCreate: creator.onCreate,
        }
      : undefined,
  });

  return (
    <PopoverContent
      className={cn("w-full p-0", className)}
      align={align}
      style={style}
      onWheelCapture={(e) => e.stopPropagation()}
    >
      <Command shouldFilter={shouldFilter}>
        {showSearch && (
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
            onKeyDown={handleKeyDown}
          />
        )}
        <CommandList>
          <CommandEmpty>
            {isLoading ? (
              <div className="flex items-center justify-center w-full">
                <Spinner size="sm" color="secondary" />
              </div>
            ) : (
              emptyText
            )}
          </CommandEmpty>
          <CommandGroup>
            {canCreate && creator && (
              <CommandItem value={search} onSelect={handleCreate}>
                {creator.render(search)}
              </CommandItem>
            )}
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  disabled={
                    option.disabled || (!isSelected && selectedValues.length >= (maxCount ?? Number.MAX_SAFE_INTEGER))
                  }
                  onSelect={() => {
                    handleToggle(option.value);
                    setSearch("");
                  }}
                  keywords={searchKeywords ? searchKeywords(option) : [option.label, option.value]}
                >
                  {renderOption ? (
                    renderOption(option, isSelected)
                  ) : (
                    <>
                      {showCheckIndicators && (
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <CheckIcon className="h-2 w-2 text-primary-foreground" />
                        </div>
                      )}
                      {option.label}
                    </>
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );
};
